import { DEFAULT_BREEZE_ENDPOINT, DEFAULT_BREEZE_PATH } from "@microsoft/applicationinsights-common";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { getConfig } from "./config-loader";
import { appStore } from "index";
let appInsightPromise = null;
const instrumentationKey = getConfig("REACT_APP_APPINSIGHT_INSTRUMENTATION_KEY");
const role = getConfig("REACT_APP_APPINSIGHT_ROLENAME");
let usingAdblock = null;
export const reactPlugin = new ReactPlugin();
const namePrefixesToMarkAsInternal = [
    "UX.",
    "EFRONT.",
    "ALADDIN."
];
const addInternalTag = (authenticatedId) => {
    const username = appStore.getState()?.auth?.oidc?.user?.profile?.ins_ux_user_login ?? "";
    if (username && !authenticatedId.startsWith("INT:") && namePrefixesToMarkAsInternal.some((x) => username.startsWith(x))) {
        return `INT:${authenticatedId}`;
    }
    return authenticatedId;
};
const addInternalTagToAppInsights = (appInsights) => {
    appInsights.context.user.authenticatedId = addInternalTag(appInsights.context.user.authenticatedId ?? "");
    return appInsights;
};
const getExcludedDomains = () => {
    try {
        const { hostname } = new URL(getConfig("REACT_APP_OIDC_AUTHORITY"));
        return [hostname];
    }
    catch {
        return [];
    }
};
const telemetryInitializer = (envelope) => {
    if (envelope.tags) {
        envelope.tags["ai.cloud.role"] = role;
        envelope.tags["ai.cloud.roleInstance"] = role;
        envelope.tags["ai.user.authUserId"] = addInternalTag(envelope.tags["ai.user.authUserId"]);
        if (usingAdblock) {
            envelope.tags["ai.cloud.adblockActive"] = true;
        }
    }
};
const checkForAdblock = () => {
    const flaggedUrl = DEFAULT_BREEZE_ENDPOINT + DEFAULT_BREEZE_PATH;
    const request = new Request(flaggedUrl, {
        body: JSON.stringify({
            testData: "test-data"
        }),
        method: "POST",
        mode: "no-cors"
    });
    return fetch(request)
        .then(() => {
        usingAdblock = false;
    })
        .catch(() => {
        usingAdblock = true;
    });
};
const initAppInsights = () => checkForAdblock()
    .then(() => {
    const config = {
        correlationHeaderExcludedDomains: getExcludedDomains(),
        disableFetchTracking: false,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        extensionConfig: {
            [reactPlugin.identifier]: { history }
        },
        extensions: [reactPlugin],
        instrumentationKey
    };
    if (usingAdblock) {
        config.endpointUrl = "/app-insights";
    }
    const ai = new ApplicationInsights({ config });
    ai.loadAppInsights();
    ai.addTelemetryInitializer(telemetryInitializer);
    return ai;
});
const getAppInsights = () => {
    if (appInsightPromise !== null) {
        return appInsightPromise;
    }
    appInsightPromise = initAppInsights();
    return appInsightPromise;
};
const appInsightsTrackEventInternal = (event, customProperties) => getAppInsights()
    .then((appInsights) => addInternalTagToAppInsights(appInsights).trackEvent(event, customProperties));
const appInsightsTrackTraceInternal = (trace, customProperties) => getAppInsights()
    .then((appInsights) => addInternalTagToAppInsights(appInsights).trackTrace(trace, customProperties));
const appInsightsTrackExceptionInternal = (exception, customProperties) => getAppInsights()
    .then((appInsights) => addInternalTagToAppInsights(appInsights).trackException(exception, customProperties));
const appInsightsPrepareAuthenticatedUserContextInternal = (authenticatedUserId, accountId, storeInCookie) => getAppInsights()
    .then((appInsights) => {
    appInsights.clearAuthenticatedUserContext();
    appInsights.setAuthenticatedUserContext(addInternalTag(authenticatedUserId ?? ""), accountId, storeInCookie);
});
export const appInsightsTrackEvent = (event, customProperties) => appInsightsTrackEventInternal(event, customProperties);
export const appInsightsTrackTrace = (trace, customProperties) => appInsightsTrackTraceInternal(trace, customProperties);
export const appInsightsTrackException = (exception, customProperties) => appInsightsTrackExceptionInternal(exception, customProperties);
export const appInsightsPrepareAuthenticatedUserContext = (authenticatedUserId, accountId, storeInCookie) => appInsightsPrepareAuthenticatedUserContextInternal(authenticatedUserId, accountId, storeInCookie);
